import React from 'react'

// Pointer-following riso ink. Mounted once at the layout level so it follows
// the cursor on every page. Fixed-position, pointer-events:none, hidden on
// touch devices via CSS (@media hover: none in _2026.scss).
//
// Magnetic behaviour:
//   On every mousemove we resolve the topmost interactive element under the
//   pointer via document.elementFromPoint(). If one is found, the blob's
//   position and size both lerp toward that element's bounding box (with a
//   small halo). When the pointer leaves the element, the blob lerps back
//   to a circle that follows the pointer.
//
// Honours `prefers-reduced-motion` via the CSS guard in _2026.scss
// (`.cursor-blob { display: none }`), and is hidden on touch via
// `@media (hover: none)`.

const BASE_SIZE = 220
const HALO = 36
const MIN_MAGNET_SIZE = 140
const MAGNET_SELECTOR =
  'a, button, [role="radio"], [role="button"], input, select, textarea, summary, [data-magnetic]'

const CursorBlob = () => {
  const blobRef = React.useRef(null)

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const blob = blobRef.current
    if (!blob) return

    let pointerX = window.innerWidth / 2
    let pointerY = window.innerHeight / 2
    let x = pointerX
    let y = pointerY
    let w = BASE_SIZE
    let h = BASE_SIZE
    let magnet = null
    let raf

    const findMagnet = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY)
      if (!el) return null
      const hit = el.closest(MAGNET_SELECTOR)
      if (!hit) return null
      // Skip elements that aren't really visible (e.g. honeypot inputs).
      const r = hit.getBoundingClientRect()
      if (r.width < 4 || r.height < 4) return null
      return hit
    }

    const onMove = e => {
      pointerX = e.clientX
      pointerY = e.clientY
      magnet = findMagnet(pointerX, pointerY)
    }

    const onLeave = () => {
      blob.style.opacity = 0
      magnet = null
    }
    const onEnter = () => {
      blob.style.opacity = ''
    }

    const tick = () => {
      let tx = pointerX
      let ty = pointerY
      let tw = BASE_SIZE
      let th = BASE_SIZE

      if (magnet && magnet.isConnected) {
        const r = magnet.getBoundingClientRect()
        tx = r.left + r.width / 2
        ty = r.top + r.height / 2
        tw = Math.max(MIN_MAGNET_SIZE, r.width + HALO * 2)
        th = Math.max(MIN_MAGNET_SIZE, r.height + HALO * 2)
      } else if (magnet && !magnet.isConnected) {
        magnet = null
      }

      const lerp = magnet ? 0.18 : 0.12
      x += (tx - x) * lerp
      y += (ty - y) * lerp
      w += (tw - w) * lerp
      h += (th - h) * lerp

      blob.style.width = w + 'px'
      blob.style.height = h + 'px'
      blob.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
      raf = requestAnimationFrame(tick)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseleave', onLeave)
    window.addEventListener('mouseenter', onEnter)
    tick()

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('mouseenter', onEnter)
    }
  }, [])

  return <div className='cursor-blob' ref={blobRef} aria-hidden='true' />
}

export default CursorBlob
